import React, { Fragment } from 'react';
import { connect } from '../../redux/store';
import { compose, withHandlers, withState } from 'recompose';
import { Link } from '@reach/router';
import {
  TeamTable,
  TeamForm,
  I18n,
  mountTable,
  unmountTable,
  refetchTable,
} from '@kineticdata/react';
import { PageTitle } from '../shared/PageTitle';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import {
  FormComponents,
  PaginationBar,
  TableComponents,
  addToast,
} from '@kineticdata/bundle-common';

const FormLayout = ({ fields, error, buttons }) => (
  <Fragment>
    <ModalBody className="form">
      {fields.get('parentTeam')}
      {fields.get('localName')}
      {fields.get('description')}
      {error}
    </ModalBody>
    <ModalFooter>{buttons}</ModalFooter>
  </Fragment>
);

const FormButtons = props => (
  <button
    className="btn btn-success"
    type="submit"
    disabled={!props.dirty || props.submitting}
    onClick={props.submit}
  >
    {props.submitting && (
      <span className="fa fa-spinner fa-spin fa-fw" />
    )}{' '}
    <I18n>Create Team</I18n>
  </button>
);

const NameCell = ({ value, row }) => (
  <td>
    <Link to={row.get('slug')} title="Edit Team">
      {value}
    </Link>
  </td>
);

const generateAgreementCell = agreementsMap => ({ value, row }) => {
  const nameParts = row.get('name').split('::');
  return (
    <td>
      {agreementsMap.has(nameParts[0]) ? (
        <span className="badge badge-pill badge-subtle">{nameParts[0]}</span>
      ) : null}
    </td>
  );
};

const EmptyBodyRow = TableComponents.generateEmptyBodyRow({
  loadingMessage: 'Loading Teams...',
  noSearchResultsMessage:
    'No teams were found - please modify your search criteria',
  noItemsMessage: 'There are no teams to display.',
});

const generatePaginationControl = ({ pageSize, changePageSize }) => props => (
  <PaginationBar
    className="mb-3"
    paging={props.loading}
    previousPage={props.prevPage}
    nextPage={props.nextPage}
    startIndex={props.startIndex}
    endIndex={props.endIndex}
    count={props.count}
    pageSize={pageSize}
    changePageSize={changePageSize}
  />
);

export const TeamsListComponent = ({
  tableKey,
  tableType,
  modalOpen,
  toggleModal,
  filterOpen,
  setFilterOpen,
  pageSize,
  changePageSize,
  agreementsMap,
  navigate,
}) => {
  const FilterFormLayout = TableComponents.generateFilterFormLayout({
    isOpen: filterOpen,
    toggle: () => setFilterOpen(open => !open),
  });
  const PaginationControl = generatePaginationControl({
    pageSize,
    changePageSize,
  });
  const AgreementCell = generateAgreementCell(agreementsMap);
  return (
    <TeamTable
      tableKey={tableKey}
      components={{
        EmptyBodyRow,
        FilterFormLayout,
        FilterFormButtons: TableComponents.FilterFormButtons,
        TableLayout: TableComponents.SettingsTableLayout,
        PaginationControl,
      }}
      addColumns={[
        {
          value: 'agreement',
          title: 'Agreement',
          components: {
            BodyCell: AgreementCell,
          },
        },
      ]}
      alterColumns={{
        name: { components: { BodyCell: NameCell } },
        description: { sortable: false },
      }}
      columnSet={['name', 'agreement', 'description']}
      filterSet={['name']}
      filterAutoFocus={0}
      onSearch={() => () => setFilterOpen(false)}
      pageSize={pageSize}
    >
      {({ pagination, table, filter, appliedFilters, filterFormKey }) => (
        <div className="page-container">
          <div className="page-panel">
            <PageTitle
              parts={['Teams']}
              breadcrumbs={[]}
              title="Teams"
              actions={[
                {
                  label: 'Create Team',
                  icon: 'plus',
                  onClick: () => toggleModal(true),
                },
              ]}
            />
            <div className="mb-5">
              <div className="text-right mb-2">{filter}</div>
              <TableComponents.FilterPills
                filterFormKey={filterFormKey}
                appliedFilters={appliedFilters}
              />
              <div className="scroll-wrapper-h">{table}</div>
              {pagination}
            </div>
          </div>

          {/* Modal for creating a new team */}
          <Modal isOpen={!!modalOpen} toggle={() => toggleModal()} size="lg">
            <div className="modal-header">
              <h4 className="modal-title">
                <button
                  type="button"
                  className="btn btn-link btn-delete"
                  onClick={() => toggleModal()}
                >
                  <I18n>Close</I18n>
                </button>
                <span>
                  <I18n>New Team</I18n>
                </span>
              </h4>
            </div>
            <TeamForm
              formkey="team-new"
              onSave={() => team => {
                addToast(`${team.name} created successfully.`);
                refetchTable(tableKey);
                navigate(`${team.slug}`);
              }}
              components={{
                FormLayout,
                FormButtons,
                FormError: FormComponents.FormError,
              }}
              alterFields={{
                description: { component: FormComponents.TextAreaField },
              }}
            >
              {({ form, initialized }) => {
                return initialized && form;
              }}
            </TeamForm>
          </Modal>
        </div>
      )}
    </TeamTable>
  );
};

// Teams Container
export const TeamsList = compose(
  connect(state => ({ agreementsMap: state.settingsApp.agreementsMap })),
  withState('modalOpen', 'setModalOpen', false),
  withState('filterOpen', 'setFilterOpen', false),
  withHandlers({
    toggleModal: props => slug =>
      !slug || slug === props.modalOpen
        ? props.setModalOpen(false)
        : props.setModalOpen(slug),
    changePageSize: props =>
      !!props.setPageSize
        ? value => () => {
            props.setPageSize(value);
            unmountTable(props.tableKey);
            mountTable(props.tableKey);
          }
        : undefined,
  }),
)(TeamsListComponent);
